import type { LabelInValue } from 'models';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Checkbox, Col, Form, FormSection, Icon, Row, TagsInput, Text, Tooltip } from 'ui';
import type { OrganizationOption } from '../video-basic-info-form-content';
import { useTagsRules } from './hooks';

export type VideoChannelsFormValues = {
  featuredInChannels: boolean;
  organization?: OrganizationOption;
  showInChannels: boolean;
  tags?: LabelInValue[];
};

const VideoChannelsFormContent = <FormValues extends VideoChannelsFormValues>() => {
  const { t } = useTranslation();
  const tagsRules = useTagsRules();

  return (
    <FormSection title={{ children: 'Channels' }}>
      <Form.Item<VideoChannelsFormValues> dependencies={['organization', 'showInChannels']} noStyle>
        {({ getFieldsValue }) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
          const { organization, showInChannels: shouldShowInChannels }: FormValues = getFieldsValue(true);

          return (
            <>
              {organization ? (
                <Col span={24}>
                  <Form.Item<VideoChannelsFormValues>>
                    <Alert
                      title={
                        <Trans
                          components={{ strong: <Text strong /> }}
                          i18nKey="The selected organization is using <strong>{{channelType}}</strong> channels"
                          values={{
                            channelType: t(organization.customChannels ? 'custom' : 'default'),
                          }}
                        />
                      }
                    />
                  </Form.Item>
                </Col>
              ) : null}

              <Col span={24}>
                <TagsInput
                  input={{
                    labelInValue: true,
                    options: organization?.tags.map((tag) => ({ label: tag.name, value: tag.id })),
                  }}
                  item={{ label: 'Tags', name: 'tags', rules: tagsRules }}
                />
              </Col>

              <Col span={24}>
                {/* TODO: add tooltip to form.item or checkbox */}
                <Form.Item<VideoChannelsFormValues> name="showInChannels" valuePropName="checked">
                  <Checkbox>
                    <Row gutter={8}>
                      <Col>
                        <Text>Show video in Channels</Text>
                      </Col>
                      <Col>
                        <Tooltip
                          title={
                            <Trans
                              components={{ br: <br /> }}
                              i18nKey="Video will display in your organization’s channels.<br/>Everyone in your organization will be able access this video."
                            />
                          }
                        >
                          <Icon icon="question-line" />
                        </Tooltip>
                      </Col>
                    </Row>
                  </Checkbox>
                </Form.Item>
              </Col>

              {shouldShowInChannels ? (
                <Col span={24}>
                  <Form.Item<VideoChannelsFormValues> name="featuredInChannels" valuePropName="checked">
                    <Checkbox>
                      <Row gutter={8}>
                        <Col>
                          <Text>Pin video as featured in Channels</Text>
                        </Col>
                        <Col>
                          <Tooltip title="Video will appear as featured in the Channels">
                            <Icon icon="question-line" />
                          </Tooltip>
                        </Col>
                      </Row>
                    </Checkbox>
                  </Form.Item>
                </Col>
              ) : undefined}
            </>
          );
        }}
      </Form.Item>
    </FormSection>
  );
};

export default VideoChannelsFormContent;
